import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const MessageComposer = ({ selectedAgent, onSendMessage, agents }) => {
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState('normal');
  const [attachments, setAttachments] = useState([]);
  const [charCount, setCharCount] = useState(0);
  const maxChars = 2000;

  const handleMessageChange = (e) => {
    const value = e?.target?.value;
    if (value?.length <= maxChars) {
      setMessage(value);
      setCharCount(value?.length);
    }
  };

  const handleSend = () => {
    if (!selectedAgent || !message?.trim()) return;
    
    onSendMessage({
      agentId: selectedAgent,
      content: message,
      priority,
      attachments,
      timestamp: new Date()
    });

    setMessage('');
    setCharCount(0);
    setAttachments([]);
    setPriority('normal');
  };

  const handleAttachment = () => {
    const newAttachment = {
      id: Date.now(),
      name: `document_${attachments?.length + 1}.pdf`,
      size: '2.4 MB',
      type: 'application/pdf'
    };
    setAttachments([...attachments, newAttachment]);
  };

  const removeAttachment = (id) => {
    setAttachments(attachments?.filter(att => att?.id !== id));
  };

  const priorityOptions = [
    { value: 'low', label: 'Low Priority', icon: 'ArrowDown', color: 'text-muted-foreground' },
    { value: 'normal', label: 'Normal Priority', icon: 'Minus', color: 'text-primary' },
    { value: 'high', label: 'High Priority', icon: 'ArrowUp', color: 'text-warning' },
    { value: 'urgent', label: 'Urgent', icon: 'AlertCircle', color: 'text-error' }
  ];

  const selectedAgentData = agents?.find(a => a?.id === selectedAgent);

  return (
    <div className="space-y-4">
      <div className="relative">
        <textarea
          value={message}
          onChange={handleMessageChange}
          placeholder={selectedAgent ? `Type your message to ${selectedAgentData?.name}...` : 'Select an agent first...'}
          disabled={!selectedAgent}
          className="w-full h-48 md:h-56 lg:h-64 p-4 bg-background border-2 border-border rounded-xl text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-3 focus:ring-ring focus:border-primary transition-all duration-250 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Message content"
        />
        <div className="absolute bottom-3 right-3 flex items-center gap-2">
          <span className={`text-xs font-medium font-caption ${charCount > maxChars * 0.9 ? 'text-warning' : 'text-muted-foreground'}`}>
            {charCount}/{maxChars}
          </span>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {priorityOptions?.map((option) => (
          <button
            key={option?.value}
            onClick={() => setPriority(option?.value)}
            disabled={!selectedAgent}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-250
              ${priority === option?.value 
                ? 'bg-primary text-primary-foreground shadow-warm-sm' 
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }
              disabled:opacity-50 disabled:cursor-not-allowed
            `}
          >
            <Icon name={option?.icon} size={16} className={priority === option?.value ? '' : option?.color} />
            <span className="font-caption">{option?.label}</span>
          </button>
        ))}
      </div>
      {attachments?.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">Attachments ({attachments?.length})</p>
          <div className="space-y-2">
            {attachments?.map((attachment) => (
              <div key={attachment?.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name="FileText" size={20} color="var(--color-primary)" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{attachment?.name}</p>
                    <p className="text-xs text-muted-foreground">{attachment?.size}</p>
                  </div>
                </div>
                <button
                  onClick={() => removeAttachment(attachment?.id)}
                  className="p-2 rounded-lg hover:bg-background transition-colors duration-250"
                  aria-label={`Remove ${attachment?.name}`}
                >
                  <Icon name="X" size={16} color="var(--color-muted-foreground)" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          variant="outline"
          size="default"
          onClick={handleAttachment}
          disabled={!selectedAgent}
          iconName="Paperclip"
          iconPosition="left"
          className="flex-1 sm:flex-initial"
        >
          Attach File
        </Button>
        <Button
          variant="default"
          size="default"
          onClick={handleSend}
          disabled={!selectedAgent || !message?.trim()}
          iconName="Send"
          iconPosition="right"
          className="flex-1"
        >
          Send Message
        </Button>
      </div>
    </div>
  );
};

export default MessageComposer;